import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as authActions from '../auth/auth.actions';
import { push } from 'connected-react-router';
import * as tenantActions from '../tenantInfo/tenant.actions';
import { tenantHelpers } from '../tenantInfo';
import { ErrorComponent } from '../common/error-component-folder/error-component';
import { participantTypeActions } from '../participant-types';

const adminSessionType = 'portal-admin';

// The single ETO origin that is allowed to hand us a session token. We pin this on
// every inbound `message` event so no other window/frame can inject a token.
const etoOrigin = (() => {
  try { return new URL(process.env.ETO_BASE_URL).origin; }
  catch (e) { return null; }
})();

const etoTokenSubscriptions = [];
function broadcastEtoToken(tokenData) {
  etoTokenSubscriptions.forEach(s => s(tokenData));
}

// ETO opens Connect in a new tab from _RedirectParticipantPortal.aspx and stays our
// window.opener. That launcher mints the encrypted session token first-party (where
// ETO's session cookie is available under SameSite=Lax) and sends it to us via
// postMessage once we signal we're ready.
function requestEtoToken() {
  if (etoOrigin && window.opener && !window.opener.closed) {
    window.opener.postMessage(JSON.stringify({ type: 'eto-token-ready' }), etoOrigin);
    return true;
  }
  return false;
}

window.addEventListener('message', e => {
  if (etoOrigin && e.origin !== etoOrigin) {
    return;
  }
  const data = safeParseJson(e.data);
  if (data && data.type === 'eto-token') {
    broadcastEtoToken(data);
  }
});

function safeParseJson(str) {
  try {
    return JSON.parse(str + '');
  } catch(ex) {
    return null;
  }
}

export function requireEtoAuthentication(Component, opts = {}) {
  class AuthenticatedComponent extends React.Component {
    constructor(props){
      super(props);
      this.state = {
          fatalError: null,
          etoUnreachable: false
      }
    }

    processNewToken(tokenData) {
      // A token (or a token error) arrived from ETO, so we can stop polling the opener.
      this.receivedToken = true;
      if (this.tokenRequestTimer) {
        clearTimeout(this.tokenRequestTimer);
        this.tokenRequestTimer = null;
      }
      if ( tokenData.tokenError ) {
        this.setState({fatalError: tokenData.tokenError});
      }
      else {
        const { authActions, tenantActions, tenant, participantTypeActions } = this.props;
          if ( tenant && !tenant.isFetching && !tenant.err && !tenant.currentTenant ) {
            tenantActions.getCurrentTenantInfo(tokenData.enterpriseGuid)
            .then( () => {
              authActions.createEtoSession(tokenData.token)
                .then(session => {
                  participantTypeActions.fetchAllParticipantTypes();
                })
                .catch(ex => { });
            })
            .catch( tenantEx => {});
        }
      }
    }

    componentWillMount() {
      this.tokenSubscription = token => this.processNewToken(token);
      etoTokenSubscriptions.push(this.tokenSubscription);
      this.verifyAuthentication(this.props);
    }

    componentDidMount() {
      this.tokenRequestAttempts = 0;
      this.pollForEtoToken();
    }

    componentWillUnmount() {
      const i = etoTokenSubscriptions.indexOf(this.tokenSubscription);
      if (i >= 0) {
        etoTokenSubscriptions.splice(i, 1);
      }
      if (this.tokenRequestTimer) {
        clearTimeout(this.tokenRequestTimer);
      }
    }

    // Ping the ETO launcher (our opener) for the session token, retrying briefly while
    // the launcher finishes loading. Stops as soon as a token arrives (see
    // processNewToken). If there is no opener, we can't obtain a session silently.
    pollForEtoToken() {
      if (this.receivedToken) {
        return;
      }
      if (!requestEtoToken()) {
        this.setState({ etoUnreachable: true });
        return;
      }
      this.tokenRequestAttempts += 1;
      if (this.tokenRequestAttempts < 20) {
        this.tokenRequestTimer = setTimeout(() => this.pollForEtoToken(), 250);
      }
    }

    componentWillReceiveProps(nextProps) {
      this.verifyAuthentication(nextProps);
    }

    verifyAuthentication(props) {
      const { auth, authActions } = props;
      const { isAuthenticated, currentSession, authToken, isFetching, err } = auth;
      const { tenant } = this.props;
      if ( tenant && !tenant.err && !tenant.isFetching && tenant.currentTenant) {

        if(!isAuthenticated && authToken && !isFetching && err === null) {
          return authActions.getCurrentSessionFromSecureToken(authToken)
            .catch(err => { });
        }

        if(currentSession && currentSession.session && currentSession.session.type !== adminSessionType) {
          return authActions.invalidateCurrentSession();
        }

        // we've made it this far.  Verify that the token is still good before attempting
        // whatever is next.  If we can detect that the token is no longer valid, we need to invalidate
        // the current session.  Only attempt the validity check for non-empty guids.

        if(isAuthenticated && currentSession && currentSession.session && currentSession.session.type === adminSessionType &&
            authToken && authToken !== '00000000-0000-0000-0000-000000000000') {
          authActions.verifySession(authToken);
        }
      }

    }

    loadingMessage(message, isError) {
      const { level, glyph } = isError
        ? { level: 'error', glyph: 'remove-sign' }
        : { level: 'loading', glyph: 'refresh' };

      return (
        <div className={`session-loader session-loader-${level}`}>
          <div className="session-loader-message">
            <span className={`glyphicon glyphicon-${glyph}`} />
            { message }
          </div>
        </div>
      )
    }

    errorMessage(headerText, bodyText) {
      return <ErrorComponent errorHeaderText={headerText} errorBodyText={bodyText}/>
    }

    // Shown while we wait for ETO to hand us a session token. If ETO is unreachable
    // (no opener — e.g. Connect was opened directly rather than from ETO), there is
    // nothing to wait for, so we tell the user to relaunch from ETO.
    waitingForEto() {
      if (this.state.etoUnreachable) {
        return this.errorMessage(
          "Couldn't start your session",
          "Connect must be opened from ETO. Please return to ETO and open Connect again."
        );
      }
      return this.loadingMessage('Waiting on session transfer from ETO...');
    }


    render() {
      const { err, isAuthenticated, isFetching, currentSession, authToken } = this.props.auth;
      const { tenant, participantTypes } = this.props;
      const { fatalError } = this.state;

      // fatalError is a string which comes from ETO (it is called tokenError by ETO).
      // This is ETO telling us that the token given to us is not valid, so the Portal
      // session effectively hits a dead end.
      // We report what ETO told us.
      if ( fatalError ) {
        return this.errorMessage("Invalid Session", fatalError);
      }

      if ( !tenant || (tenant.isFetching && !tenant.currentTenant && !tenant.err) ) {
        return this.loadingMessage('Loading customer data...');
      }

      if ( tenant.err ) {
        // this error message is just in place until the product owner decides what he likes
        return this.loadingMessage('Could not find tenant', true);
      }

      if (err) {
          if (err.errorCode === 'unauthenticated') {
            return this.errorMessage("Session Timed Out", `Your ETO session has timed out.  To continue working with ${tenantHelpers.getProductName()}, you must log out of ETO and then log back in.`);
          }
          else {
            return this.loadingMessage(err.errorMessage || 'There was an error retrieving your session', true);
          }
      }

      if (isFetching && authToken) {
        return this.loadingMessage('Loading Session...')
      }

      if (isFetching) {
        return this.loadingMessage('Creating Session...')
      }

      var participantTypeState = participantTypes && participantTypes.get('participantTypeState');
      var participantTypesLoading = participantTypeState && participantTypeState.isFetching;

      if (participantTypesLoading) {
        return this.loadingMessage('Loading Data Context...')
      }

      if (isAuthenticated && currentSession && currentSession.session && currentSession.session.type !== adminSessionType) {
        return this.loadingMessage('Your session is of the wrong type.', true);
      }

      if (!isAuthenticated) {
        return this.waitingForEto();
      }

      return (
        <div>
          <Component {...this.props} />
        </div>
      );
    }
  }

  AuthenticatedComponent.propTypes = {
    auth: PropTypes.any.isRequired,
    authActions: PropTypes.any.isRequired,
    tenant: PropTypes.any.isRequired,
    tenantActions: PropTypes.any.isRequired,
    routing: PropTypes.any.isRequired,
    opts: PropTypes.any.isRequired,
    participantTypes: PropTypes.any.isRequired,
    participantTypeActions: PropTypes.any.isRequired,
  }

  function mapStateToProps({ auth, routing, tenant, participantTypes }) {
    return {
      auth,
      routing,
      tenant,
      opts,
      participantTypes,
    };
  }

  function mapDispatchToProps(dispatch) {
    return {
      authActions: bindActionCreators(authActions, dispatch),
      tenantActions: bindActionCreators(tenantActions, dispatch),
      redirect: bindActionCreators(push, dispatch),
      participantTypeActions: bindActionCreators(participantTypeActions, dispatch),
    };
  }

  return connect(mapStateToProps, mapDispatchToProps)(AuthenticatedComponent)
}
